import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Dialog, DialogPanel, Transition, TransitionChild, } from "@headlessui/react";
import { Fragment, useEffect, useRef } from "react";
import { Button } from "../button";
import { Heading } from "../typography/heading";
export const Drawer = ({ id, testId, className, isOpen, title, position = "left", size, onDismiss, dismissibleOnOverlay = true, noOverlayNonBlocking = false, footer, children, }) => {
    const dialogRef = useRef(null);
    let drawerSize;
    switch (size) {
        case "xs":
            drawerSize =
                position === "left" || position === "right" ? "sm:w-1/4" : "h-[25vh]";
            break;
        case "sm":
            drawerSize =
                position === "left" || position === "right" ? "sm:w-1/3" : "h-[33vh]";
            break;
        case "lg":
            drawerSize =
                position === "left" || position === "right" ? "sm:w-2/3" : "h-[66vh]";
            break;
        case "xl":
            drawerSize =
                position === "left" || position === "right" ? "sm:w-10/12" : "h-[83vh]";
            break;
        case "md":
            drawerSize =
                position === "left" || position === "right" ? "sm:w-1/2" : "h-[50vh]";
            break;
        default:
            drawerSize =
                position === "left" || position === "right" ? "w-[328px]" : "h-[328px]";
            break;
    }
    let positionStyle = "";
    let containerStyle = "";
    let dialogStyle = "";
    let panelStyle = "";
    let startAnimation = "";
    let endAnimation = "";
    let showHeader = "";
    const headerHeight = "mt-[64px]";
    switch (position) {
        case "left":
            positionStyle = "";
            containerStyle = `fixed inset-y-0 left-0 flex ${drawerSize}`;
            dialogStyle = "w-screen";
            panelStyle = "h-full pb-[64px]";
            startAnimation = "-translate-x-full";
            endAnimation = "translate-x-0 shadow-right";
            showHeader = headerHeight;
            break;
        case "right":
            positionStyle = "";
            containerStyle = `fixed inset-y-0 right-0 flex ${drawerSize}`;
            dialogStyle = "w-screen";
            panelStyle = "h-full w-full pb-[64px]";
            startAnimation = "translate-x-full";
            endAnimation = "translate-x-0 shadow-left";
            showHeader = headerHeight;
            break;
        case "top":
            positionStyle = "top-0";
            containerStyle = "w-screen";
            dialogStyle = "";
            panelStyle = `${drawerSize}`;
            startAnimation = "-translate-y-full";
            endAnimation = "translate-y-0 shadow-bottom overflow-y-hidden";
            showHeader = headerHeight;
            break;
        case "bottom":
            positionStyle = "bottom-0";
            containerStyle = "w-screen";
            dialogStyle = "";
            panelStyle = `${drawerSize} shadow-top`;
            startAnimation = "translate-y-2/3";
            endAnimation = "translate-y-0";
            showHeader = "";
            break;
        default:
            break;
    }
    containerStyle =
        className !== undefined ? `${containerStyle} ${className}` : containerStyle;
    const dismissOnOverlay = dismissibleOnOverlay ? onDismiss : () => { };
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        let timer;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (isOpen && dialogRef !== undefined) {
            timer = setTimeout(() => {
                var _a, _b, _c;
                const zDrawerElement = (_a = dialogRef.current) === null || _a === void 0 ? void 0 : _a.parentElement;
                const parentWrapper = zDrawerElement.parentElement;
                if (id !== undefined) {
                    parentWrapper.setAttribute("id", id);
                    // data-test-id={testId}
                }
                if (testId !== undefined) {
                    parentWrapper.setAttribute("data-test-id", testId);
                }
                // The headlessui Dialog injects the `inert` attribute on a parent or ancestor div which prevents all interaction with children in that container.
                // You can read more about the `inert` attribute here:
                // https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inert
                // It also add an `overflow: hidden` to the highest parent tag which prevents scrolling. Sneaky, sneaky, sneaky
                if (!dismissibleOnOverlay) {
                    (_b = document.querySelectorAll("[inert]")[0]) === null || _b === void 0 ? void 0 : _b.removeAttribute("inert");
                    let firstOverflowHidden = Array.from(document.querySelectorAll("[style*='overflow'"));
                    (_c = firstOverflowHidden[0]) === null || _c === void 0 ? void 0 : _c.style.removeProperty("overflow");
                }
            }, 300);
        }
        return () => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (timer !== undefined)
                clearTimeout(timer);
        };
    }, [isOpen]);
    return (_jsx(Transition, { show: isOpen, as: Fragment, children: _jsxs(Dialog, { as: "div", className: "z-drawer relative", "data-eco-component": "drawer", onClose: dismissOnOverlay, children: [dismissibleOnOverlay && (_jsx(TransitionChild, { as: Fragment, enter: "ease-out duration-fast", enterFrom: "opacity-0", enterTo: "opacity-40", leave: "ease-in duration-fast", leaveFrom: "opacity-40", leaveTo: "opacity-0", children: _jsx("div", { className: `fixed inset-0 bg-black/40 ${headerHeight}` }) })), _jsx("div", { ref: dialogRef, className: `fixed ${positionStyle}`, children: _jsx("div", { className: `pointer-events-none ${containerStyle}`, children: _jsx(TransitionChild, { as: Fragment, enter: "ease-out duration-normal", enterFrom: `opacity-0 ${startAnimation}`, enterTo: `opacity-100 ${endAnimation}`, leave: "ease-in duration-normal", leaveFrom: `opacity-100 ${endAnimation}`, leaveTo: `opacity-0 ${startAnimation}`, children: _jsx(DialogPanel, { className: `pointer-events-auto ${dialogStyle}`, children: _jsxs("div", { className: `${panelStyle} bg-components-baseComponent dark:bg-dark-components-baseComponent ${showHeader} flex flex-col`, children: [_jsxs("div", { className: `border-light dark:border-dark-light flex items-center ${title !== undefined ? "justify-between" : "justify-end"} gap-4 border-b p-4`, "data-eco-component": "drawer-header", children: [title !== undefined && _jsx(Heading, { as: "h5", children: title }), _jsx("div", { children: _jsx(Button, { variant: "tertiary", iconName: ["far", "xmark"], "data-eco-component": "drawer-close", onClick: onDismiss, hideLabel: true, children: "Dismiss" }) })] }), _jsx("div", { className: `relative flex-1 overflow-y-auto p-4`, "data-eco-component": "drawer-content", children: children }), footer !== undefined && (_jsx("div", { className: "border-light dark:border-dark-light flex w-full items-center justify-end gap-4 border-t p-4", "data-eco-component": "drawer-footer", children: footer }))] }) }) }) }) })] }) }));
};
